export interface WhatsAppGroup {
  id: string;
  tenant_id: string;
  name: string;
  invite_link: string;
  current_members: number;
  max_capacity: number;
  priority: number;
  status: 'active' | 'standby' | 'overflow' | 'desynced';
  can_send?: boolean;
  permission_error?: string | null;
  jid?: string | null;
  instance_name?: string | null;
  phone_number?: string | null;
  total_clicks?: number;
  messages_sent?: number;
  dispatch_start_hour?: number;
  dispatch_end_hour?: number;
  min_delay_minutes?: number;
  max_delay_minutes?: number;
  last_synced_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AvailableWhatsAppGroup {
  jid: string;
  name: string;
  current_members: number;
  invite_link?: string | null;
  is_admin: boolean;
  can_send?: boolean;
  permission_error?: string | null;
  already_imported: boolean;
}

export interface GroupImportItem {
  jid: string;
  name: string;
  invite_link: string;
  current_members: number;
  max_capacity: number;
  priority: number;
  status?: 'active' | 'standby' | 'overflow' | 'desynced';
  dispatch_start_hour?: number;
  dispatch_end_hour?: number;
  min_delay_minutes?: number;
  max_delay_minutes?: number;
}

export interface GroupImportPayload {
  groups: GroupImportItem[];
}

export interface GroupImportResult {
  imported_count: number;
  skipped_count: number;
  groups: WhatsAppGroup[];
  message: string;
}

export interface GroupMetrics {
  group_id: string;
  group_name: string;
  total_clicks: number;
  messages_sent: number;
  active_promotions: number;
  current_members: number;
  max_capacity: number;
  occupancy_rate: number;
  instance_name?: string | null;
  phone_number?: string | null;
  is_line_connected: boolean;
  status: string;
}

export interface WhatsAppGroupCreatePayload {
  name: string;
  invite_link: string;
  max_capacity: number;
  priority: number;
  current_members?: number;
  status?: 'active' | 'standby' | 'overflow' | 'desynced';
  jid?: string | null;
  dispatch_start_hour?: number;
  dispatch_end_hour?: number;
  min_delay_minutes?: number;
  max_delay_minutes?: number;
}

export interface WhatsAppGroupUpdatePayload {
  name?: string;
  invite_link?: string;
  max_capacity?: number;
  priority?: number;
  current_members?: number;
  status?: 'active' | 'standby' | 'overflow' | 'desynced';
  jid?: string | null;
  dispatch_start_hour?: number;
  dispatch_end_hour?: number;
  min_delay_minutes?: number;
  max_delay_minutes?: number;
}

export interface GroupSyncResult {
  group_id: string;
  synced: boolean;
  current_members: number;
  status: 'active' | 'standby' | 'overflow' | 'desynced';
  message: string;
  name?: string;
}

export interface GroupSyncAllResult {
  total_groups: number;
  synced_count: number;
  overflow_count: number;
  message: string;
}

const API_BASE_URL = '';

export async function fetchGroups(token: string): Promise<WhatsAppGroup[]> {
  const response = await fetch(`${API_BASE_URL}/api/groups`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    throw new Error(`Falha ao listar grupos (${response.status})`);
  }

  return response.json();
}

export async function createGroup(
  token: string,
  payload: WhatsAppGroupCreatePayload
): Promise<WhatsAppGroup> {
  const response = await fetch(`${API_BASE_URL}/api/groups`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string' ? detail : 'Erro ao cadastrar grupo.';
    throw new Error(msg);
  }

  return response.json();
}

export async function updateGroup(
  token: string,
  groupId: string,
  payload: WhatsAppGroupUpdatePayload
): Promise<WhatsAppGroup> {
  const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string' ? detail : 'Erro ao atualizar grupo.';
    throw new Error(msg);
  }

  return response.json();
}

export async function deleteGroup(
  token: string,
  groupId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg = typeof detail === 'string' ? detail : 'Erro ao excluir grupo.';
    throw new Error(msg);
  }

  return response.json();
}

export async function syncGroup(
  token: string,
  groupId: string
): Promise<GroupSyncResult> {
  const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/sync`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Erro ao sincronizar grupo (${response.status})`);
  }

  return response.json();
}

export async function syncAllGroups(
  token: string
): Promise<GroupSyncAllResult> {
  const response = await fetch(`${API_BASE_URL}/api/groups/sync-all`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Erro ao sincronizar grupos (${response.status})`);
  }

  return response.json();
}

export async function fetchAvailableGroups(
  token: string
): Promise<AvailableWhatsAppGroup[]> {
  const response = await fetch(`${API_BASE_URL}/api/groups/available`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar grupos disponíveis (${response.status})`);
  }

  return response.json();
}

export async function importGroups(
  token: string,
  payload: GroupImportPayload
): Promise<GroupImportResult> {
  const response = await fetch(`${API_BASE_URL}/api/groups/import`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string' ? detail : 'Erro ao importar grupos.';
    throw new Error(msg);
  }

  return response.json();
}

export async function clearAllGroups(
  token: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/groups/clear-all`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg = typeof detail === 'string' ? detail : 'Erro ao limpar grupos.';
    throw new Error(msg);
  }

  return response.json();
}

export async function fetchGroupMetrics(
  token: string,
  groupId: string
): Promise<GroupMetrics> {
  const response = await fetch(
    `${API_BASE_URL}/api/groups/${groupId}/metrics`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Erro ao carregar métricas do grupo (${response.status})`);
  }

  return response.json();
}

export async function relinkGroup(
  token: string,
  groupId: string
): Promise<WhatsAppGroup> {
  const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/relink`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Erro ao revincular grupo à linha atual (${response.status})`
    );
  }

  return response.json();
}

export async function resolveGroupLink(
  token: string,
  inviteLink: string
): Promise<AvailableWhatsAppGroup> {
  const response = await fetch(`${API_BASE_URL}/api/groups/resolve-link`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ invite_link: inviteLink }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string' ? detail : 'Erro ao obter dados do link.';
    throw new Error(msg);
  }

  return response.json();
}

export async function resolveGroupJid(
  token: string,
  jid: string
): Promise<AvailableWhatsAppGroup> {
  const response = await fetch(`${API_BASE_URL}/api/groups/resolve-jid`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ jid }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : 'Erro ao obter dados do grupo via WhatsApp.';
    throw new Error(msg);
  }

  return response.json();
}
